import {InfiniteData, useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {benefactorsApi} from '../api';
import {BenefactorOrdersFilter, BenefactorOrderWithMetaModel} from '../model';
import {useCallback, useMemo, useState} from 'react';

type BenefactorOrdersState = {
  getInfiniteBenefactorOrders: UseInfiniteQueryResult<InfiniteData<BenefactorOrderWithMetaModel>, Error>;
  filters: BenefactorOrdersFilter;
  updateFilter: (newFilter: Partial<BenefactorOrdersFilter>) => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useBenefactorOrders = (
  benefactorId: string,
  initialFilter: BenefactorOrdersFilter = {},
): BenefactorOrdersState => {
  const [filters, setFilters] = useState<BenefactorOrdersFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteBenefactorOrders = useInfiniteQuery<
    BenefactorOrderWithMetaModel,
    Error,
    InfiniteData<BenefactorOrderWithMetaModel>,
    ['benefactor-orders', string, 'infinite', Omit<BenefactorOrdersFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['benefactor-orders', benefactorId, 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return benefactorsApi.getBenefactorOrders(benefactorId, {
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
    enabled: !!benefactorId,
  });

  const updateFilter = useCallback((newFilter: Partial<BenefactorOrdersFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  return {
    getInfiniteBenefactorOrders,
    filters,
    updateFilter,
  };
};
